import { LoadingButton } from '@mui/lab';
import {
  Card,
  Chip,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionButton, PermissionGuard } from 'src/routes/components';
import { Resources } from '../resource';
import { displayLabel } from '../perps-v1-shared/api';
import { PERPS_V1_SECTORS_PERMISSION } from '../perps-v1-shared/constants';
import { errorMessage } from '../perps-v1-shared/errorMessage';
import { MarketSector } from '../perps-v1-shared/types';
import SectorEditorDialog from './components/SectorEditorDialog';
import { sectorsApi } from './dataProvider';
import { viewsToWarnAbout } from './disableWarning';
import './init';

// Next slot at the END of the sector list: max(existing displayOrder) + 1, or 1 for an empty
// list. Used to seed a new sector's displayOrder now that the editor dialog no longer has a
// Display order input — mirrors perps-v1-views/List.tsx's nextDisplayOrder.
function nextDisplayOrder(sectors: MarketSector[]): number {
  if (sectors.length === 0) return 1;
  return Math.max(...sectors.map((s) => s.displayOrder)) + 1;
}

const scopeKey = (scope: MarketSector['scope']): string => {
  switch (scope) {
    case 'hip3':
      return 'module.perpsV1Sectors.scopeHip3';
    case 'any':
      return 'module.perpsV1Sectors.scopeAny';
    default:
      return 'module.perpsV1Sectors.scopeNative';
  }
};

const View = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [sectors, setSectors] = useState<MarketSector[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSector, setEditingSector] = useState<MarketSector | null>(null);
  // displayOrder to seed the create form with — see nextDisplayOrder. Computed at the moment
  // "New sector" is clicked (openCreate), from the full sectors array already held by the list.
  const [createDisplayOrder, setCreateDisplayOrder] = useState(1);

  const loadSectors = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dataProvider.getList(Resources.perpsV1Sectors, {
        filter: {},
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'displayOrder', order: 'ASC' },
      });
      const data: MarketSector[] = response?.data ?? [];
      // displayOrder has no visible column anymore (marketSectors isn't consumed by the app's
      // tab bar; that comes from marketNavigation/view order instead), but this sort still does
      // real work: it keeps the seeded Native block (1-6) ahead of the HIP-3 block (7-11), and
      // this same `sectors` array feeds OTHER screens' pickers — notably the Symbols screen's
      // Sector filter dropdown. Do not replace with a code/name sort.
      setSectors([...data].sort((a, b) => a.displayOrder - b.displayOrder));
    } catch (error: any) {
      notify(
        errorMessage(error, translate('module.perpsV1Sectors.loadError')),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }, [dataProvider, notify, translate]);

  useEffect(() => {
    loadSectors();
  }, [loadSectors]);

  const handleToggleEnabled = async (sector: MarketSector, enabled: boolean) => {
    const affectedViews = viewsToWarnAbout(enabled, sector.referencingViewCodes);
    if (affectedViews.length > 0) {
      // eslint-disable-next-line no-alert
      if (
        !window.confirm(
          translate('module.perpsV1Sectors.disableConfirm', { views: affectedViews.join(', ') })
        )
      ) {
        return;
      }
    }
    try {
      await sectorsApi.setEnabled(sector.code, enabled);
      await loadSectors();
    } catch (error: any) {
      notify(
        errorMessage(error, translate('module.perpsV1Shared.saveError')),
        { type: 'error' }
      );
    }
  };

  const handleDelete = async (sector: MarketSector) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(translate('module.perpsV1Shared.confirmDelete'))) return;
    try {
      await sectorsApi.remove(sector.code);
      notify(translate('module.perpsV1Shared.saveSuccess'), { type: 'success' });
      await loadSectors();
    } catch (error: any) {
      notify(
        errorMessage(error, translate('module.perpsV1Shared.saveError')),
        { type: 'error' }
      );
    }
  };

  const openCreate = () => {
    setEditingSector(null);
    setCreateDisplayOrder(nextDisplayOrder(sectors));
    setDialogOpen(true);
  };

  const openEdit = (sector: MarketSector) => {
    setEditingSector(sector);
    setDialogOpen(true);
  };

  return (
    <PermissionGuard resource={PERPS_V1_SECTORS_PERMISSION} action="read">
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.perpsV1Sectors.title')}
          links={[{ name: translate('resource.perpsV1') }, { name: translate('resource.perpsV1Sectors') }]}
          action={
            <PermissionButton resource={PERPS_V1_SECTORS_PERMISSION} action="create">
              <LoadingButton variant="contained" onClick={openCreate}>
                {translate('module.perpsV1Sectors.newSector')}
              </LoadingButton>
            </PermissionButton>
          }
          sx={{ mb: 1 }}
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {translate('module.perpsV1Sectors.subtitle')}
        </Typography>

        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{translate('module.perpsV1Sectors.columns.code')}</TableCell>
                  <TableCell>{translate('module.perpsV1Sectors.columns.name')}</TableCell>
                  <TableCell>{translate('module.perpsV1Sectors.columns.scope')}</TableCell>
                  <TableCell>{translate('module.perpsV1Sectors.columns.source')}</TableCell>
                  <TableCell>{translate('module.perpsV1Sectors.columns.enabled')}</TableCell>
                  <TableCell align="right">{translate('module.perpsV1Shared.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && sectors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {translate('module.perpsV1Sectors.empty')}
                    </TableCell>
                  </TableRow>
                ) : null}
                {sectors.map((sector) => (
                  <TableRow key={sector.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {sector.code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{displayLabel(sector.name, sector.code)}</Typography>
                      {sector.name?.zh ? (
                        <Typography variant="caption" color="text.secondary">
                          {sector.name.zh}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={translate(scopeKey(sector.scope))} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={sector.hasAutoSource ? 'info' : 'default'}
                        label={translate(
                          sector.hasAutoSource
                            ? 'module.perpsV1Sectors.sourceAuto'
                            : 'module.perpsV1Sectors.sourceManual'
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <PermissionButton resource={PERPS_V1_SECTORS_PERMISSION} action="update">
                        <Switch
                          size="small"
                          checked={sector.enabled}
                          onChange={(e) => handleToggleEnabled(sector, e.target.checked)}
                        />
                      </PermissionButton>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                        <PermissionButton resource={PERPS_V1_SECTORS_PERMISSION} action="update">
                          <IconButton size="small" onClick={() => openEdit(sector)}>
                            <Iconify icon="eva:edit-2-outline" />
                          </IconButton>
                        </PermissionButton>
                        <PermissionButton resource={PERPS_V1_SECTORS_PERMISSION} action="delete">
                          <Tooltip title={translate('module.perpsV1Sectors.deleteHint')}>
                            <IconButton size="small" color="error" onClick={() => handleDelete(sector)}>
                              <Iconify icon="eva:trash-2-outline" />
                            </IconButton>
                          </Tooltip>
                        </PermissionButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <SectorEditorDialog
          open={dialogOpen}
          sector={editingSector}
          createDisplayOrder={createDisplayOrder}
          onClose={() => setDialogOpen(false)}
          onSaved={loadSectors}
        />
      </DashboardContent>
    </PermissionGuard>
  );
};

export default View;
